import React, { useState, useEffect, useCallback } from 'react';
import {
  startPlacement,
  submitPlacement,
  startSkillPractice,
  submitSkillPractice,
  getRecommendedQuestions,
  checkPracticeAnswer,
  getPracticeHint,
  startCheckin,
  PlacementQuestion,
  PlacementResult,
  PlacementSubmitAnswer,
  RecommendedQuestion,
  CheckAnswerResult,
} from '../api/placement';
import { track } from '../api/analytics';
import { redeemStreakMakeup } from '../api/streak';
import { questionToHtml, renderOption } from '../utils/richText';
import { useAuth } from '../contexts/AuthContext';
import ScoreReport from './ScoreReport';
import { QuizTopBar, QuizProgressStrip, QuizOption, QuizNavBar, QuizBackButton, QuizPrimaryButton } from './quiz/QuizAtoms';
import { playSuccess } from '../utils/sound';
import { ExtractsBlock } from './quiz/QuizTypeRenderers';
import './PlacementQuiz.css';

// ─── Domain label map ─────────────────────────────────────────────────────────
const DOMAIN_LABEL: Record<string, string> = {
  numeracy: 'Maths',
  maths: 'Maths',
  mathematics: 'Maths',
  reading: 'Reading',
  language_conventions: 'Language',
  language: 'Language',
  writing: 'Writing',
};

function domainLabel(domain: string): string {
  return DOMAIN_LABEL[domain.toLowerCase()] ?? domain;
}

// The DB `title` column is a SHORT (truncated, "…") label, not the full prompt —
// rendering it cut students off mid-question. The complete stem lives in
// content.question / questionText / stem, so prefer those and only fall back to
// title when content has no text. (See ROO question-display bug.)
function questionStem(q: PlacementQuestion): string {
  const c = (q.content || {}) as Record<string, unknown>;
  const pick = (v: unknown) =>
    typeof v === 'string' && v.trim() ? v.trim() : '';
  return (
    pick(c.question) ||
    pick(c.questionText) ||
    pick(c.stem) ||
    pick(c.text) ||
    q.title ||
    ''
  );
}

// ─── Arrow icon (inline, matches dashboard style) ─────────────────────────────
const ArrowRight = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14m-6-6 6 6-6 6" />
  </svg>
);

const ArrowLeft = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M19 12H5m6 6-6-6 6-6" />
  </svg>
);

// ─── Props ────────────────────────────────────────────────────────────────────
export interface PlacementQuizProps {
  studentId: string;
  onComplete: () => void;
  onCancel: () => void;
  /** Optional "Practise now" CTA on the result report (ROO-132). */
  onPractise?: () => void;
  /**
   * 'practice' runs a targeted by-skill set (no band recalibration) (ROO-133).
   * 'recommended' runs the wrong-answer-driven recommended set (ROO-160).
   * 'checkin' runs the 5-question cross-domain mini check-in (ROO-171).
   */
  mode?: 'placement' | 'practice' | 'recommended' | 'checkin';
  skill?: string;
  /** Per-skill "Practise this" from the result report (ROO-133). */
  onPractiseSkill?: (area: string, label: string) => void;
  /**
   * Streak makeup (ROO-221): a missed day (YYYY-MM-DD) to repair. When set on
   * a check-in, submitting the quiz redeems the makeup automatically.
   */
  makeupDate?: string;
}

// ─── Resume cache (ROO-130) ───────────────────────────────────────────────────
// Persist in-progress placement to localStorage so a reload offers Continue/Start
// new instead of silently losing answers.
interface SavedPlacement {
  questions: PlacementQuestion[];
  answers: PlacementSubmitAnswer[];
  currentIdx: number;
  savedAt: number;
}
const placementKey = (sid: string) => `cleveroo_placement_${sid}`;
const RESUME_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function loadSavedPlacement(sid: string): SavedPlacement | null {
  try {
    const raw = localStorage.getItem(placementKey(sid));
    if (!raw) return null;
    const s = JSON.parse(raw) as SavedPlacement;
    if (!s || !Array.isArray(s.questions) || s.questions.length === 0) return null;
    if (!Array.isArray(s.answers) || s.answers.length === 0) return null; // no real progress
    if (s.savedAt && Date.now() - s.savedAt > RESUME_MAX_AGE_MS) return null; // stale
    return s;
  } catch {
    return null;
  }
}
function savePlacement(sid: string, data: SavedPlacement): void {
  try { localStorage.setItem(placementKey(sid), JSON.stringify(data)); } catch { /* quota */ }
}
function clearSavedPlacement(sid: string): void {
  try { localStorage.removeItem(placementKey(sid)); } catch { /* noop */ }
}

// ─── Component ───────────────────────────────────────────────────────────────
const PlacementQuiz: React.FC<PlacementQuizProps> = ({
  studentId,
  onComplete,
  onCancel,
  onPractise,
  mode = 'placement',
  skill,
  onPractiseSkill,
  makeupDate,
}) => {
  const isRecommended = mode === 'recommended';
  const isCheckin = mode === 'checkin';
  // Practice semantics (fresh start, no resume cache, practice submit) apply to
  // by-skill practice, the recommended set and the mini check-in.
  const isPractice = mode !== 'placement';
  const { user } = useAuth();
  // Extract first name for the score report (e.g. "Alice Smith" → "Alice")
  const studentFirstName = user?.name ? user.name.split(' ')[0] : null;

  const [phase, setPhase] = useState<'loading' | 'resume' | 'quiz' | 'submitting' | 'result' | 'error'>('loading');
  const [questions, setQuestions] = useState<PlacementQuestion[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState<PlacementSubmitAnswer[]>([]);
  const [result, setResult] = useState<PlacementResult | null>(null);
  // Streak-makeup redemption outcome, shown on the result screen (ROO-221).
  const [makeupOutcome, setMakeupOutcome] = useState<'ok' | 'failed' | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [saved, setSaved] = useState<SavedPlacement | null>(null);
  // questionId → why it was recommended (weak-tag labels + redo flag) (ROO-160)
  const [recoMeta, setRecoMeta] = useState<Record<string, { redo: boolean; reasons: string[] }>>({});
  // Instant feedback per checked question (practice modes only, ROO-167).
  const [feedback, setFeedback] = useState<Record<string, CheckAnswerResult>>({});
  const [checking, setChecking] = useState(false);
  // Staged tutor hints per question (practice modes only, ROO-170).
  const [hints, setHints] = useState<Record<string, string[]>>({});
  const [hintLoading, setHintLoading] = useState(false);

  // Start (or restart) a fresh session, discarding any saved progress.
  const fetchFresh = useCallback(() => {
    setPhase('loading');
    if (!isPractice) clearSavedPlacement(studentId);
    setSaved(null);
    const load = isRecommended
      ? getRecommendedQuestions(studentId)
      : isCheckin
      ? startCheckin(studentId)
      : isPractice
      ? startSkillPractice(studentId, skill || '')
      : startPlacement(studentId);
    load
      .then((session) => {
        const qs = session.questions || [];
        // Empty set → show a friendly message, never an empty quiz (white screen).
        // Practice-by-skill hits this when a subject has no questions yet
        // (e.g. Thinking/Writing), so guard every mode, not just recommended.
        if (qs.length === 0) {
          setErrorMsg(
            isRecommended
              ? "Nothing to practise right now — you're all caught up! Take a new assessment to find fresh challenges."
              : isPractice
              ? "No practice questions for this skill just yet. Try another subject, or take an assessment to unlock more."
              : 'This quiz has no questions available right now. Please try again later.'
          );
          setPhase('error');
          return;
        }
        if (isRecommended) {
          const meta: Record<string, { redo: boolean; reasons: string[] }> = {};
          for (const q of qs as RecommendedQuestion[]) {
            meta[q.id] = { redo: !!q.redo, reasons: (q.reasons || []).map((r) => r.label) };
          }
          setRecoMeta(meta);
        }
        setQuestions(qs);
        setAnswers([]);
        setCurrentIdx(0);
        setPhase('quiz');
        if (!isPractice) {
          track('placementStarted', {
            properties: { studentId, questionCount: qs.length },
          });
        }
      })
      .catch((err) => {
        setErrorMsg(
          err?.response?.data?.error ||
            err?.message ||
            (isPractice ? 'Failed to load practice. Please try again.' : 'Failed to load placement quiz. Please try again.')
        );
        setPhase('error');
      });
  }, [studentId, isPractice, isRecommended, isCheckin, skill]);

  // On mount: practice always starts fresh; placement offers to resume saved progress.
  useEffect(() => {
    if (isPractice) { fetchFresh(); return; }
    const savedProgress = loadSavedPlacement(studentId);
    if (savedProgress) {
      setSaved(savedProgress);
      setPhase('resume');
    } else {
      fetchFresh();
    }
  }, [studentId, isPractice, fetchFresh]);

  // Continue from saved progress.
  const handleResume = useCallback(() => {
    if (!saved) { fetchFresh(); return; }
    setQuestions(saved.questions);
    setAnswers(saved.answers);
    setCurrentIdx(Math.min(saved.currentIdx, Math.max(0, saved.questions.length - 1)));
    setPhase('quiz');
  }, [saved, fetchFresh]);

  // Persist progress to localStorage as the user answers / navigates (placement only).
  useEffect(() => {
    if (isPractice || phase !== 'quiz' || questions.length === 0) return;
    savePlacement(studentId, { questions, answers, currentIdx, savedAt: Date.now() });
  }, [isPractice, phase, questions, answers, currentIdx, studentId]);

  // Fire scoreReportViewed once when the placement result screen is shown.
  useEffect(() => {
    if (phase === 'result' && result) {
      track('scoreReportViewed', {
        properties: {
          context: 'placementQuiz',
          scorePercent: result.scorePercent,
          recommendedBand: result.recommendedBand,
        },
      });
    }
  }, [phase, result]);

  // ── helpers ──
  const total = questions.length;
  const progressPct = total > 0 ? Math.round((currentIdx / total) * 100) : 0;

  const currentQuestion: PlacementQuestion | undefined = questions[currentIdx];

  // multiple_choice questions allow selecting several options; single_choice one.
  const isMulti = currentQuestion?.type === 'multiple_choice';

  const answerEntry = answers.find((a) => a.questionId === currentQuestion?.id)?.value;
  const selectedIds: string[] =
    answerEntry == null ? [] : Array.isArray(answerEntry) ? answerEntry : [answerEntry];
  const hasAnswer = selectedIds.length > 0;

  // Feedback state for the current question (practice modes; undefined = not checked yet).
  const currentFeedback = currentQuestion ? feedback[currentQuestion.id] : undefined;
  const currentHints = (currentQuestion && hints[currentQuestion.id]) || [];

  // Ask for the next staged hint (max 3, never reveals the answer) (ROO-170).
  const handleHint = async () => {
    if (!currentQuestion || hintLoading || currentHints.length >= 3) return;
    setHintLoading(true);
    try {
      const r = await getPracticeHint(studentId, currentQuestion.id, currentHints.length + 1, currentHints);
      if (r?.hint) {
        setHints((prev) => ({
          ...prev,
          [currentQuestion.id]: [...(prev[currentQuestion.id] || []), r.hint],
        }));
      }
    } catch {
      /* hint is best-effort */
    } finally {
      setHintLoading(false);
    }
  };

  // "Check answer" — grade the committed answer for instant feedback (ROO-167).
  const handleCheck = async () => {
    if (!currentQuestion || !hasAnswer || currentFeedback || checking) return;
    setChecking(true);
    try {
      const value = isMulti ? selectedIds : selectedIds[0];
      const result = await checkPracticeAnswer(studentId, currentQuestion.id, value);
      setFeedback((prev) => ({ ...prev, [currentQuestion.id]: result }));
      if (result.isCorrect) playSuccess();
    } catch {
      // Feedback is best-effort — fall back to plain Next on failure.
      setFeedback((prev) => ({
        ...prev,
        [currentQuestion.id]: { isCorrect: false, correctIds: [], explanation: null, options: [] },
      }));
    } finally {
      setChecking(false);
    }
  };

  // single_choice: replace the selection. multiple_choice: toggle the id in/out.
  const selectOption = (value: string) => {
    if (!currentQuestion) return;
    // Answer is locked once feedback has been revealed (practice modes).
    if (feedback[currentQuestion.id]) return;
    setAnswers((prev) => {
      const next = prev.filter((a) => a.questionId !== currentQuestion.id);
      if (isMulti) {
        const updated = selectedIds.includes(value)
          ? selectedIds.filter((v) => v !== value)
          : [...selectedIds, value];
        return updated.length
          ? [...next, { questionId: currentQuestion.id, value: updated }]
          : next;
      }
      return [...next, { questionId: currentQuestion.id, value }];
    });
  };

  const handleNext = () => {
    if (currentIdx < total - 1) {
      setCurrentIdx((i) => i + 1);
    }
  };

  const handleBack = () => {
    if (currentIdx > 0) {
      setCurrentIdx((i) => i - 1);
    }
  };

  const handleFinish = async () => {
    setPhase('submitting');
    try {
      const res = isPractice
        ? await submitSkillPractice(studentId, answers, skill || '')
        : await submitPlacement(studentId, answers);
      // Streak makeup (ROO-221): quiz done → claim the missed day right away
      // (not on report close, so closing the tab can't lose the makeup).
      if (makeupDate) {
        try {
          const mk = await redeemStreakMakeup(studentId, makeupDate);
          setMakeupOutcome(mk.madeUp || mk.alreadyActive ? 'ok' : 'failed');
        } catch {
          setMakeupOutcome('failed');
        }
      }
      if (!isPractice) {
        clearSavedPlacement(studentId); // completed — drop the resume cache
        track('placementCompleted', {
          properties: {
            studentId,
            scorePercent: res.scorePercent,
            recommendedBand: res.recommendedBand,
            correct: res.correct,
            totalQuestions: res.totalQuestions,
          },
        });
      }
      setResult(res);
      setPhase('result');
    } catch (err: any) {
      setErrorMsg(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to submit quiz. Please try again.'
      );
      setPhase('error');
    }
  };

  const isLastQuestion = currentIdx === total - 1;

  const quizTitle = isRecommended ? 'Recommended Practice'
    : isCheckin ? 'Quick Check-in'
    : isPractice ? 'Skill Practice'
    : 'Placement Quiz';

  // ── render: resume prompt (ROO-130) ──
  if (phase === 'resume' && saved) {
    return (
      <div className="cvr-portal pq-shell">
        <QuizTopBar title={quizTitle} onCancel={onCancel} />
        <div className="pq-wrap">
          <div className="pq-resume">
            <h2 className="pq-resume__title">Continue your placement quiz?</h2>
            <p className="pq-resume__text">
              You answered {saved.answers.length} of {saved.questions.length} questions last time.
            </p>
            <div className="pq-resume__actions">
              <button className="pq-resume__primary" type="button" onClick={handleResume}>
                Continue
              </button>
              <button className="pq-resume__secondary" type="button" onClick={fetchFresh}>
                Start new
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── render: loading ──
  if (phase === 'loading') {
    return (
      <div className="cvr-portal pq-shell">
        <QuizTopBar title={quizTitle} onCancel={onCancel} />
        <div className="pq-wrap">
          <div className="pq-loading">
            <div className="pq-loading__spinner" />
            Loading your quiz...
          </div>
        </div>
      </div>
    );
  }

  // ── render: error ──
  if (phase === 'error') {
    return (
      <div className="cvr-portal pq-shell">
        <QuizTopBar title={quizTitle} onCancel={onCancel} cancelLabel="Back" />
        <div className="pq-wrap">
          <div className="pq-error">{errorMsg}</div>
          <div style={{ marginTop: 20 }}>
            <button
              className="pq-nav__back"
              type="button"
              onClick={onCancel}
            >
              {ArrowLeft} Go back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── render: result ──
  if (phase === 'result' && result) {
    return (
      <div className="cvr-portal pq-shell">
        <QuizTopBar title={quizTitle} right="Complete!" />
        <QuizProgressStrip pct={100} />
        <div className="pq-result">
          <h1 className="pq-result__headline" style={{ marginBottom: 24 }}>
            {isPractice ? 'Practice complete!' : 'You’re calibrated!'}
          </h1>
          {makeupOutcome === 'ok' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', background: '#F5FBF0', border: '1.5px solid #CDE7BC', borderRadius: 12, padding: '10px 18px', margin: '0 auto 20px', maxWidth: 440, color: '#14593C', fontWeight: 700, fontSize: 14.5 }}>
              <span aria-hidden="true">🔥</span> Day made up — your streak is repaired!
            </div>
          )}
          {makeupOutcome === 'failed' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', background: '#FFF4E0', border: '1.5px solid #F2C87E', borderRadius: 12, padding: '10px 18px', margin: '0 auto 20px', maxWidth: 460, color: '#7A4E12', fontWeight: 600, fontSize: 13.5 }}>
              <span aria-hidden="true">⚠️</span> Quiz done, but the day couldn&rsquo;t be claimed — try tapping the missed day again.
            </div>
          )}
          <ScoreReport
            result={result}
            studentName={studentFirstName}
            onClose={onComplete}
            onPractise={onPractise}
            onPractiseSkill={onPractiseSkill}
            // Band is a placement outcome; a 5-question warm-up or practice
            // set shouldn't re-declare the student's starting level (ROO-219).
            showBand={mode === 'placement'}
          />
        </div>
      </div>
    );
  }

  // ── render: quiz (also covers submitting — disable buttons) ──
  if (!currentQuestion) return null;

  const submitting = phase === 'submitting';

  return (
    <div className="cvr-portal pq-shell">
      <QuizTopBar
        title={quizTitle}
        onCancel={onCancel}
        cancelDisabled={submitting}
        right={<>Question {currentIdx + 1} of {total}</>}
      />
      <QuizProgressStrip pct={progressPct} />

      <div className="pq-wrap">
        {submitting && (
          <div className="pq-loading">
            <div className="pq-loading__spinner" />
            Submitting your answers...
          </div>
        )}

        {!submitting && (
          <div className="pq-question">
            {(() => {
              const content = currentQuestion.content as Record<string, any>;
              const p = content?.passage;
              // Reading comprehension: questions sharing a passageId each embed the
              // same passage, so rendering it keeps it on-screen across them (ROO-129).
              const passage = p && (p.title || p.content) ? p : null;

              const main = (
                <>
                  {currentQuestion.subject && (
                    <div className="pq-question__subject">{domainLabel(currentQuestion.subject)}</div>
                  )}
                  {/* Why this question was recommended (ROO-160) */}
                  {isRecommended && recoMeta[currentQuestion.id] && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '2px 0 10px' }}>
                      {recoMeta[currentQuestion.id].redo && (
                        <span style={{ padding: '2px 10px', borderRadius: 999, background: '#FDEBD2', color: '#9A6215', fontSize: 11.5, fontWeight: 700 }}>
                          ↻ Try again
                        </span>
                      )}
                      {recoMeta[currentQuestion.id].reasons.map((label) => (
                        <span key={label} style={{ padding: '2px 10px', borderRadius: 999, background: '#EAF3E2', color: '#14593C', fontSize: 11.5, fontWeight: 600 }}>
                          Boost: {label}
                        </span>
                      ))}
                    </div>
                  )}
                  {(() => {
                    const stemText = questionStem(currentQuestion);
                    const stemHtml = questionToHtml(content, stemText);
                    return stemHtml ? (
                      <div className="pq-question__text" dangerouslySetInnerHTML={{ __html: stemHtml }} />
                    ) : (
                      <div className="pq-question__text">{stemText}</div>
                    );
                  })()}
                  {/* Multi-extract reading comparison — shared block (ROO-180) */}
                  <ExtractsBlock extracts={content?.extracts} />
                  {(() => {
                    const imgs = Array.isArray(content?.images) ? content.images : [];
                    return imgs.length > 0 ? (
                      <div className="pq-question__figure">
                        {imgs.map((im: any, i: number) => (
                          <img key={i} src={im.src || im.url} alt={im.alt || `Figure ${i + 1}`} />
                        ))}
                      </div>
                    ) : null;
                  })()}
                  {isMulti && <div className="pq-question__hint">Select all that apply</div>}
                  <div className="pq-options" role={isMulti ? 'group' : 'radiogroup'} aria-label="Answer options">
                    {(currentQuestion.answers || []).map((opt, oi) => {
                      const selected = selectedIds.includes(opt.value);
                      // Instant-feedback states once checked (ROO-167).
                      const fb = currentFeedback;
                      const isCorrectOpt = fb ? fb.correctIds.includes(opt.value) : false;
                      return (
                        <QuizOption
                          key={opt.value}
                          selected={selected}
                          multi={isMulti}
                          onClick={() => selectOption(opt.value)}
                          feedback={fb ? { isCorrectOption: isCorrectOpt, isWrongPick: selected && !isCorrectOpt } : null}
                          img={opt.img}
                          html={renderOption(opt.text)}
                          text={opt.text}
                          index={oi + 1}
                        />
                      );
                    })}
                  </div>

                  {/* Tutor hints (practice modes, before checking) (ROO-170) */}
                  {isPractice && !currentFeedback && (
                    <div style={{ marginTop: 12 }}>
                      {currentHints.length > 0 && (
                        <div
                          style={{
                            borderRadius: 12,
                            border: '1.5px solid #E8DFC5',
                            background: '#FBF7EA',
                            padding: '10px 14px',
                            marginBottom: 8,
                          }}
                        >
                          {currentHints.map((h, i) => {
                            const html = renderOption(h);
                            return (
                              <p key={i} style={{ margin: i === 0 ? 0 : '8px 0 0', fontSize: 13.5, color: '#5B5030' }}>
                                <b style={{ color: '#9A6215' }}>Hint {i + 1}:</b>{' '}
                                {html ? <span dangerouslySetInnerHTML={{ __html: html }} /> : h}
                              </p>
                            );
                          })}
                        </div>
                      )}
                      {currentHints.length < 3 && (
                        <button
                          type="button"
                          onClick={handleHint}
                          disabled={hintLoading}
                          style={{
                            border: '1.5px dashed #C9BC8F',
                            background: 'none',
                            color: '#9A6215',
                            borderRadius: 999,
                            padding: '5px 14px',
                            fontSize: 12.5,
                            fontWeight: 700,
                            cursor: hintLoading ? 'wait' : 'pointer',
                          }}
                        >
                          {hintLoading
                            ? 'Thinking…'
                            : currentHints.length === 0
                            ? '💡 Need a hint?'
                            : '💡 Another hint'}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Instant feedback panel (practice modes, ROO-167) */}
                  {currentFeedback && (
                    <div
                      style={{
                        marginTop: 14,
                        borderRadius: 12,
                        border: `1.5px solid ${currentFeedback.isCorrect ? '#C5E0B0' : '#F0C4B4'}`,
                        background: currentFeedback.isCorrect ? '#F5FBF0' : '#FDF3EF',
                        padding: '12px 16px',
                      }}
                    >
                      <div style={{ fontWeight: 800, fontSize: 14, color: currentFeedback.isCorrect ? '#14593C' : '#B83E24', marginBottom: 4 }}>
                        {currentFeedback.isCorrect ? '✓ Correct — nice work!' : '✗ Not quite'}
                      </div>
                      {(() => {
                        // Why the chosen wrong option is wrong (single line), then the
                        // worked solution. Rendered as rich text so LaTeX in the
                        // generated explanations displays properly.
                        const wrongPick = currentFeedback.options.find(
                          (o) => selectedIds.includes(o.id) && !o.isCorrect && o.whyWrong,
                        );
                        const richText = (text: string, color: string, mb = 0) => {
                          const h = renderOption(text);
                          const style: React.CSSProperties = { margin: `0 0 ${mb}px`, fontSize: 13.5, color };
                          return h ? (
                            <p style={style} dangerouslySetInnerHTML={{ __html: h }} />
                          ) : (
                            <p style={style}>{text}</p>
                          );
                        };
                        return (
                          <>
                            {!currentFeedback.isCorrect && wrongPick?.whyWrong &&
                              richText(wrongPick.whyWrong, '#7A4633', 6)}
                            {currentFeedback.explanation &&
                              richText(currentFeedback.explanation, '#3D4A40')}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </>
              );

              if (!passage) return main;

              const bodyStr = String(passage.content || '');
              const bodyHtml = questionToHtml({}, bodyStr);
              const byline: string[] = [];
              if (passage.author) byline.push(`by ${passage.author}`);
              else if (passage.source) byline.push(`Source: ${passage.source}`);
              if (passage.wordCount) byline.push(`${passage.wordCount} words`);

              return (
                <div className="pq-reading">
                  <aside className="pq-passage">
                    {passage.title && <h3 className="pq-passage__title">{passage.title}</h3>}
                    {byline.length > 0 && <div className="pq-passage__byline">{byline.join(' · ')}</div>}
                    {bodyHtml ? (
                      <div className="pq-passage__body" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                    ) : (
                      <div className="pq-passage__body">
                        {bodyStr.split(/\n\s*\n/).map((para, i) => <p key={i}>{para}</p>)}
                      </div>
                    )}
                  </aside>
                  <div className="pq-reading__main">{main}</div>
                </div>
              );
            })()}

            {/* Navigation — shared shell primitives (ROO-180) */}
            <QuizNavBar
              left={
                <QuizBackButton onClick={handleBack} disabled={currentIdx === 0}>
                  {ArrowLeft} Back
                </QuizBackButton>
              }
              right={
                /* Practice modes check the answer first (instant feedback, ROO-167);
                   placement keeps the straight Next/Finish flow. */
                isPractice && hasAnswer && !currentFeedback ? (
                  <QuizPrimaryButton onClick={handleCheck} disabled={checking}>
                    {checking ? 'Checking…' : 'Check answer'} {ArrowRight}
                  </QuizPrimaryButton>
                ) : isLastQuestion ? (
                  <QuizPrimaryButton onClick={handleFinish} disabled={!hasAnswer} finish>
                    Finish {ArrowRight}
                  </QuizPrimaryButton>
                ) : (
                  <QuizPrimaryButton onClick={handleNext} disabled={!hasAnswer}>
                    Next {ArrowRight}
                  </QuizPrimaryButton>
                )
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PlacementQuiz;
